import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {
    ContentCopy as ContentCopyIcon,
    Save as SaveIcon,
    Share as ShareIcon
} from '@mui/icons-material';

import { apiClient } from '../../utils/api';

interface PrivateRealInfo {
    real_name?: string;
    age?: number | '';
    occupation?: string;
    education?: string;
    hometown?: string;
    relationship_goal?: string;
    lifestyle?: string;
    interests?: string;
    values?: string;
    contact_preference?: string;
}

interface PrivateInfoResponse {
    real_info: PrivateRealInfo;
    share_enabled: boolean;
    share_path: string;
}

const emptyInfo: PrivateRealInfo = {
    real_name: '',
    age: '',
    occupation: '',
    education: '',
    hometown: '',
    relationship_goal: '',
    lifestyle: '',
    interests: '',
    values: '',
    contact_preference: ''
};

const AvatarShareSettings: React.FC = () => {
    const [info, setInfo] = useState<PrivateRealInfo>(emptyInfo);
    const [sharePath, setSharePath] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const shareUrl = useMemo(() => {
        if (!sharePath) return '';
        return `${window.location.origin}${sharePath}`;
    }, [sharePath]);

    const qrUrl = useMemo(() => {
        if (!shareUrl) return '';
        return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(shareUrl)}`;
    }, [shareUrl]);

    useEffect(() => {
        loadInfo();
    }, []);

    const loadInfo = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await apiClient.get<PrivateInfoResponse>('/api/v1/avatar/private-info/me');
            setInfo({ ...emptyInfo, ...data.real_info });
            setSharePath(data.share_path);
        } catch (err: any) {
            setError(err.message || 'Failed to load private info');
        } finally {
            setLoading(false);
        }
    };

    const updateField = (field: keyof PrivateRealInfo, value: string) => {
        setInfo(prev => ({
            ...prev,
            [field]: field === 'age' ? (value === '' ? '' : Number(value)) : value
        }));
    };

    const saveInfo = async () => {
        try {
            setSaving(true);
            setMessage(null);
            setError(null);
            const payload = {
                ...info,
                age: info.age === '' ? null : info.age
            };
            const data = await apiClient.put<PrivateInfoResponse>('/api/v1/avatar/private-info/me', payload);
            setInfo({ ...emptyInfo, ...data.real_info });
            setSharePath(data.share_path);
            setMessage('Private information saved. Your normal platform profile still does not display it.');
        } catch (err: any) {
            setError(err.message || 'Failed to save private info');
        } finally {
            setSaving(false);
        }
    };

    const copyShareUrl = async () => {
        if (!shareUrl) return;
        await navigator.clipboard.writeText(shareUrl);
        setMessage('Share link copied.');
    };

    if (loading) {
        return (
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h5" component="h2" gutterBottom>
                            Private Real Information
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            This information is not shown in normal discovery/profile surfaces. It is included only on your shared avatar report page.
                        </Typography>
                    </Box>

                    {message && <Alert severity="success" onClose={() => setMessage(null)}>{message}</Alert>}
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Real name" value={info.real_name || ''} onChange={e => updateField('real_name', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" label="Age" value={info.age ?? ''} onChange={e => updateField('age', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Occupation" value={info.occupation || ''} onChange={e => updateField('occupation', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Education" value={info.education || ''} onChange={e => updateField('education', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Hometown" value={info.hometown || ''} onChange={e => updateField('hometown', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Contact preference" value={info.contact_preference || ''} onChange={e => updateField('contact_preference', e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline minRows={2} label="Relationship goal" value={info.relationship_goal || ''} onChange={e => updateField('relationship_goal', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth multiline minRows={3} label="Lifestyle" value={info.lifestyle || ''} onChange={e => updateField('lifestyle', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth multiline minRows={3} label="Interests" value={info.interests || ''} onChange={e => updateField('interests', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth multiline minRows={3} label="Values" value={info.values || ''} onChange={e => updateField('values', e.target.value)} />
                        </Grid>
                    </Grid>

                    <Box display="flex" gap={1} flexWrap="wrap">
                        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} onClick={saveInfo} disabled={saving}>
                            {saving ? 'Saving...' : 'Save private info'}
                        </Button>
                        <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyShareUrl} disabled={!shareUrl}>
                            Copy share link
                        </Button>
                        <Button variant="outlined" startIcon={<ShareIcon />} href={sharePath || undefined} target="_blank" disabled={!sharePath}>
                            Open report
                        </Button>
                    </Box>

                    {shareUrl && (
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <Box component="img" src={qrUrl} alt="Avatar report QR code" sx={{ width: 180, height: 180, border: '1px solid', borderColor: 'divider' }} />
                            </Grid>
                            <Grid item xs>
                                <Typography variant="subtitle2" gutterBottom>
                                    Avatar report QR code
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                    {shareUrl}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default AvatarShareSettings;
